// Victory celebration overlay rendered when the player wins. Variant chosen
// by the user's active_victory_anim cosmetic. CSS-only (Framer Motion) so
// it's cheap and works on mobile-web.
//
// Variants:
//   default    — classic confetti pop (simple bursts)
//   fireworks  — 4 colored rocket bursts at staggered positions
//   lightning  — vertical bolt flashes around the win line
//
// Usage: <VictoryAnimation variant={profile?.active_victory_anim} />
import React from "react";
import { motion } from "framer-motion";

const COLORS = ["#FFD700", "#00E5FF", "#FF2A2A", "#B537F2", "#3DDC97"];

function Confetti() {
  // 24 particles bursting outward from screen center
  const N = 24;
  return (
    <div className="fixed inset-0 z-[125] pointer-events-none" data-testid="victory-anim-default">
      {Array.from({ length: N }).map((_, i) => {
        const angle = (i / N) * Math.PI * 2;
        const dist = 120 + Math.random() * 220;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        return (
          <motion.span
            key={i}
            className="absolute"
            style={{
              left: "50%", top: "50%",
              width: 7, height: 12,
              background: COLORS[i % COLORS.length],
              borderRadius: 2,
              boxShadow: `0 0 8px ${COLORS[i % COLORS.length]}`,
            }}
            initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
            animate={{ x: dx, y: dy, opacity: 0, rotate: 360 + Math.random() * 720 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          />
        );
      })}
    </div>
  );
}

function Fireworks() {
  // 4 staggered firework bursts at different screen positions
  const bursts = [
    { x: "25%", y: "30%", delay: 0,    color: "#FFD700" },
    { x: "70%", y: "35%", delay: 0.3,  color: "#00E5FF" },
    { x: "40%", y: "60%", delay: 0.6,  color: "#FF2A2A" },
    { x: "75%", y: "65%", delay: 0.9,  color: "#B537F2" },
  ];
  return (
    <div className="fixed inset-0 z-[125] pointer-events-none" data-testid="victory-anim-fireworks">
      {bursts.map((b, bi) => (
        <div key={bi} className="absolute" style={{ left: b.x, top: b.y }}>
          {Array.from({ length: 14 }).map((_, i) => {
            const angle = (i / 14) * Math.PI * 2;
            const r = 80 + Math.random() * 100;
            return (
              <motion.span
                key={i}
                className="absolute"
                style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: b.color,
                  boxShadow: `0 0 12px ${b.color}`,
                }}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{
                  x: Math.cos(angle) * r,
                  y: Math.sin(angle) * r,
                  opacity: 0,
                  scale: 0.4,
                }}
                transition={{ duration: 1.4, delay: b.delay, ease: "easeOut" }}
              />
            );
          })}
          {/* Center flash */}
          <motion.span
            className="absolute rounded-full"
            style={{
              width: 30, height: 30, left: -15, top: -15,
              background: b.color,
              boxShadow: `0 0 40px ${b.color}`,
            }}
            initial={{ opacity: 0.9, scale: 0.5 }}
            animate={{ opacity: 0, scale: 2 }}
            transition={{ duration: 0.6, delay: b.delay }}
          />
        </div>
      ))}
    </div>
  );
}

function Lightning() {
  // 5 vertical bolt flashes across the screen with slight horizontal jitter
  const bolts = Array.from({ length: 5 }).map((_, i) => ({
    x: 12 + i * 18 + Math.random() * 8,
    delay: i * 0.18,
    color: i % 2 ? "#00E5FF" : "#FFFFFF",
  }));
  return (
    <div className="fixed inset-0 z-[125] pointer-events-none overflow-hidden" data-testid="victory-anim-lightning">
      {bolts.map((b, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            left: `${b.x}%`,
            top: 0,
            width: 3,
            height: "100%",
            background: `linear-gradient(180deg, transparent, ${b.color} 30%, ${b.color} 70%, transparent)`,
            boxShadow: `0 0 24px ${b.color}, 0 0 48px ${b.color}`,
            filter: "blur(0.5px)",
          }}
          initial={{ opacity: 0, scaleY: 0.4 }}
          animate={{ opacity: [0, 1, 0, 1, 0], scaleY: [0.4, 1, 1, 1, 0.6] }}
          transition={{ duration: 1.2, delay: b.delay, ease: "easeOut" }}
        />
      ))}
      {/* Full-screen flash overlay */}
      <motion.div
        className="absolute inset-0"
        style={{ background: "rgba(255,255,255,0.18)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.6, 0, 0.4, 0] }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      />
    </div>
  );
}

export default function VictoryAnimation({ variant }) {
  const v = variant || "default";
  if (v === "fireworks") return <Fireworks />;
  if (v === "lightning") return <Lightning />;
  return <Confetti />;
}
